/**
 * Bulk import engine (Documents > Importar): customer migrations from another system.
 *
 * A .zip is read lazily: only its central directory, then one entry at a time, so an archive of several GB works.
 * A folder or a set of files is read the same way (the browser hands over disk-backed File objects). An optional
 * manifest.csv / manifesto.csv adds title, original date, status, signers and the old system's id to each file.
 * Every file is hashed (SHA-256): that is its import key, so a re-run of the same zip skips what is already there.
 * Batches of up to 20 files go to the API; each batch is stored inside its own request.
 */
import { getApiUrl } from "./config";

export interface ImportConfig {
  allowed: boolean;
  reason?: string | null;
  maxFileBytes: number;
  maxFilesPerBatch: number;
  maxBatchBytes: number;
  maxDocumentsPerImport: number;
  extensions: string[];
}

export interface ImportRecord {
  id: string;
  name: string;
  sourceSystem?: string | null;
  status: "open" | "completed" | "cancelled" | "undone";
  expectedCount: number;
  expectedBytes: number;
  importedCount: number;
  duplicateCount: number;
  failedCount: number;
  importedBytes: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
}

export interface ImportSigner { name?: string; email?: string; signedAt?: string }

/** What the manifest says about one file. */
export interface ManifestMeta {
  title?: string;
  date?: string;
  status?: string;
  externalId?: string;
  signers?: ImportSigner[];
}

export interface ScanItem {
  path: string;
  name: string;
  size: number;
  date?: Date;
  meta?: ManifestMeta;
  read: () => Promise<Blob>;
}

export type SkipCode = "unsupported_type" | "too_large" | "empty" | "encrypted" | "unreadable";

export interface SkippedItem { path: string; code: SkipCode; detail?: string }

export interface ManifestInfo {
  name: string;
  rows: number;
  matched: number;
  unmatched: string[];
  error?: "no_file_column" | "empty" | "unreadable";
}

export interface ScanResult {
  sourceName: string;
  items: ScanItem[];
  skipped: SkippedItem[];
  /** System files that are never documents (__MACOSX, .DS_Store, Thumbs.db, Office lock files). */
  ignored: number;
  totalBytes: number;
  manifest?: ManifestInfo;
}

export class ImportError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

// ── API ────────────────────────────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function unwrap<T>(json: unknown): T {
  if (json && typeof json === "object" && "success" in json && "data" in json) return (json as { data: T }).data;
  return json as T;
}

async function errorFrom(res: Response): Promise<ImportError> {
  let message = res.statusText;
  let code: string | undefined;
  try {
    const body = await res.json();
    const pt = (localStorage.getItem("language") || navigator.language || "pt").toLowerCase().startsWith("pt");
    message = (pt && body?.messagePt) || body?.message || message;
    code = body?.errorCode || body?.reason;
  } catch { /* not JSON */ }
  return new ImportError(res.status, message, code);
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(getApiUrl(path), {
    method,
    headers: { ...authHeaders(), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return undefined as T;
  return unwrap<T>(await res.json());
}

export const importApi = {
  config: () => call<ImportConfig>("GET", "/api/documents/imports/config"),
  list: () => call<ImportRecord[]>("GET", "/api/documents/imports"),
  get: (id: string) => call<{ import: ImportRecord; errors: { path: string; code: string; detail?: string; createdAt: string }[] }>("GET", `/api/documents/imports/${id}`),
  create: (body: { name: string; sourceSystem?: string; expectedCount: number; expectedBytes: number }) => call<ImportRecord>("POST", "/api/documents/imports", body),
  complete: (id: string, cancelled: boolean) => call<ImportRecord>("POST", `/api/documents/imports/${id}/complete`, { cancelled }),
  undo: (id: string) => call<{ deleted: number; remaining: number; kept: number; status: string }>("POST", `/api/documents/imports/${id}/undo`),
  /** Saves the import's error report (CSV that opens in Excel). */
  async downloadErrors(id: string, fileName: string): Promise<void> {
    const res = await fetch(getApiUrl(`/api/documents/imports/${id}/errors.csv`), { headers: authHeaders() });
    if (!res.ok) throw await errorFrom(res);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

// ── paths and file types ───────────────────────────────────────────────────────────────────

export const SUPPORTED_EXTENSIONS = [".pdf", ".docx", ".doc"];
const MIME: Record<string, string> = {
  ".pdf": "application/pdf",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".doc": "application/msword",
};
const MANIFEST_NAMES = new Set(["manifesto.csv", "manifest.csv"]);

const slash = (p: string) => p.replace(/\\/g, "/");
export const baseName = (p: string) => { const s = slash(p); return s.slice(s.lastIndexOf("/") + 1); };
const extOf = (p: string) => { const b = baseName(p); const i = b.lastIndexOf("."); return i > 0 ? b.slice(i).toLowerCase() : ""; };
const stripExt = (name: string) => { const i = name.lastIndexOf("."); return i > 0 ? name.slice(0, i) : name; };
/** Comparable form of a path: NFC, forward slashes, no leading ./ or /, lower case. */
const normPath = (p: string) => slash(p.normalize("NFC")).replace(/^(\.\/|\/)+/, "").trim().toLowerCase();

function isJunk(path: string): boolean {
  const p = slash(path);
  const b = baseName(p).toLowerCase();
  return p.split("/").includes("__MACOSX") || b === ".ds_store" || b === "thumbs.db" || b === "desktop.ini" || b.startsWith("._") || b.startsWith("~$");
}

const mb = (bytes: number) => `${(bytes / 1024 / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;

function classify(path: string, size: number, cfg: ImportConfig): SkippedItem | null {
  const ext = extOf(path);
  if (!SUPPORTED_EXTENSIONS.includes(ext)) return { path, code: "unsupported_type", detail: ext || "?" };
  if (size <= 0) return { path, code: "empty" };
  if (size > cfg.maxFileBytes) return { path, code: "too_large", detail: `${mb(size)} > ${mb(cfg.maxFileBytes)}` };
  return null;
}

// ── text decoding ──────────────────────────────────────────────────────────────────────────

/** Upper half of IBM code page 850, what Windows tools in Brazil use for zip names without the UTF-8 flag. */
const CP850 = "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ ";

function decodeZipName(value: Uint8Array, encoding: string, type: "filename" | "comment"): string | undefined {
  if (type !== "filename" || /^utf-?8$/i.test(encoding)) return undefined;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(value);
  } catch {
    let s = "";
    for (const b of value) s += b < 128 ? String.fromCharCode(b) : CP850[b - 128];
    return s;
  }
}

/** CSV bytes: UTF-8 (with or without BOM) or Windows-1252, what Excel saves in Brazil. */
function decodeText(bytes: Uint8Array): string {
  const start = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0;
  const body = bytes.subarray(start);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(body);
  } catch {
    return new TextDecoder("windows-1252").decode(body);
  }
}

// ── manifest ───────────────────────────────────────────────────────────────────────────────

/** RFC 4180-ish CSV; the delimiter (; , or tab) comes from the header line. */
export function parseCsv(text: string): string[][] {
  const end = text.search(/\r?\n/);
  const header = end < 0 ? text : text.slice(0, end);
  const counts: Record<string, number> = { ";": 0, ",": 0, "\t": 0 };
  let quoted = false;
  for (const ch of header) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && ch in counts) counts[ch]++;
  }
  const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  const d = delimiter[1] > 0 ? delimiter[0] : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"') inQuotes = true;
    else if (c === d) { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c !== "\r") field += c;
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((f) => f.trim() !== ""));
}

const headerKey = (h: string) => h.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim().replace(/[\s-]+/g, "_").replace(/[^a-z0-9_]/g, "");

const COLUMNS = {
  file: ["arquivo", "file", "caminho", "path", "nome_do_arquivo", "nome_arquivo", "filename", "file_name"],
  title: ["titulo", "title", "nome_do_documento", "document_name", "documento", "assunto", "nome"],
  date: ["data", "date", "data_do_documento", "data_documento", "created_at", "criado_em", "data_de_criacao"],
  status: ["status", "situacao"],
  signers: ["signatarios", "signers", "assinantes", "signatario", "signer"],
  emails: ["emails", "email", "e_mails", "e_mail"],
  signedAt: ["assinado_em", "signed_at", "data_assinatura", "data_da_assinatura"],
  externalId: ["id_externo", "external_id", "id", "codigo", "code", "referencia"],
};

export const MANIFEST_TEMPLATE =
  "﻿arquivo;titulo;data;status;signatarios;emails;assinado_em;id_externo\r\n" +
  "2024/03/Contrato 0001.pdf;Contrato de prestação de serviços 0001;15/03/2024 10:30;assinado;Ana Souza|Bruno Dias;ana@exemplo.com.br|bruno@exemplo.com.br;16/03/2024 09:12;SIS-0001\r\n";

function readManifest(text: string): { rows: { file: string; meta: ManifestMeta }[]; error?: ManifestInfo["error"] } {
  const table = parseCsv(text);
  if (table.length < 2) return { rows: [], error: "empty" };
  const header = table[0].map(headerKey);
  const col = (aliases: string[]) => { for (const a of aliases) { const i = header.indexOf(a); if (i >= 0) return i; } return -1; };
  const at = {
    file: col(COLUMNS.file), title: col(COLUMNS.title), date: col(COLUMNS.date), status: col(COLUMNS.status),
    signers: col(COLUMNS.signers), emails: col(COLUMNS.emails), signedAt: col(COLUMNS.signedAt), externalId: col(COLUMNS.externalId),
  };
  if (at.file < 0) return { rows: [], error: "no_file_column" };
  const cell = (r: string[], i: number) => (i >= 0 && i < r.length ? r[i].trim() : "");
  const list = (v: string) => (v ? v.split("|").map((s) => s.trim()) : []);
  const rows: { file: string; meta: ManifestMeta }[] = [];
  for (const r of table.slice(1)) {
    const file = cell(r, at.file);
    if (!file) continue;
    const names = list(cell(r, at.signers));
    const emails = list(cell(r, at.emails));
    const signed = list(cell(r, at.signedAt));
    const count = Math.max(names.length, emails.length);
    const signers: ImportSigner[] = [];
    for (let k = 0; k < count; k++) {
      const signedAt = signed[k] ?? (signed.length === 1 ? signed[0] : undefined);
      signers.push({ name: names[k] || undefined, email: emails[k] || undefined, signedAt: signedAt || undefined });
    }
    rows.push({
      file,
      meta: {
        title: cell(r, at.title) || undefined,
        date: cell(r, at.date) || undefined,
        status: cell(r, at.status) || undefined,
        externalId: cell(r, at.externalId) || undefined,
        signers: signers.length ? signers : undefined,
      },
    });
  }
  return { rows };
}

/** Rows find their file by full path, by path inside a top folder, or by a unique file name. */
function attachManifest(items: ScanItem[], name: string, text: string | null): ManifestInfo {
  if (text == null) return { name, rows: 0, matched: 0, unmatched: [], error: "unreadable" };
  const { rows, error } = readManifest(text);
  if (error) return { name, rows: 0, matched: 0, unmatched: [], error };
  const byPath = new Map<string, ScanItem>();
  const byBase = new Map<string, ScanItem[]>();
  for (const it of items) {
    const p = normPath(it.path);
    byPath.set(p, it);
    const b = baseName(p);
    const list = byBase.get(b);
    if (list) list.push(it); else byBase.set(b, [it]);
  }
  let matched = 0;
  const unmatched: string[] = [];
  for (const row of rows) {
    const p = normPath(row.file);
    let item = byPath.get(p);
    if (!item) {
      const same = byBase.get(baseName(p)) ?? [];
      const inside = same.filter((it) => normPath(it.path).endsWith("/" + p));
      item = inside.length === 1 ? inside[0] : !p.includes("/") && same.length === 1 ? same[0] : undefined;
    }
    if (item) { item.meta = row.meta; matched++; } else if (unmatched.length < 50) unmatched.push(row.file);
  }
  return { name, rows: rows.length, matched, unmatched };
}

// ── scanning ───────────────────────────────────────────────────────────────────────────────

function finish(sourceName: string, items: ScanItem[], skipped: SkippedItem[], ignored: number, manifest?: ManifestInfo): ScanResult {
  return { sourceName, items, skipped, ignored, totalBytes: items.reduce((s, i) => s + i.size, 0), manifest };
}

const depth = (p: string) => slash(p).split("/").length;

/** Reads a .zip's directory; entries are decompressed only when sent. */
export async function scanZip(file: File, cfg: ImportConfig): Promise<ScanResult> {
  // Reader core with native (de)compression streams: no WebAssembly, no workers (nothing to host or allow in a CSP).
  const zip = await import("@zip.js/zip.js/lib/zip-core-native.js");
  zip.configure({ useWebWorkers: false });
  const reader = new zip.ZipReader(new zip.BlobReader(file), { decodeText: decodeZipName });
  let entries: Awaited<ReturnType<typeof reader.getEntries>>;
  try {
    entries = await reader.getEntries();
  } catch {
    throw new ImportError(0, "bad_zip", "bad_zip");
  }
  const items: ScanItem[] = [];
  const skipped: SkippedItem[] = [];
  let ignored = 0;
  let manifestEntry: (typeof entries)[number] | null = null;
  for (const entry of entries) {
    if (entry.directory) continue;
    const path = entry.filename;
    if (isJunk(path)) { ignored++; continue; }
    if (MANIFEST_NAMES.has(baseName(path).toLowerCase())) {
      if (!manifestEntry || depth(path) < depth(manifestEntry.filename)) manifestEntry = entry;
      continue;
    }
    const bad = classify(path, entry.uncompressedSize, cfg);
    if (bad) { skipped.push(bad); continue; }
    if (entry.encrypted) { skipped.push({ path, code: "encrypted" }); continue; }
    const fileEntry = entry;
    const mime = MIME[extOf(path)];
    items.push({ path, name: baseName(path), size: entry.uncompressedSize, date: entry.lastModDate, read: () => fileEntry.getData(new zip.BlobWriter(mime)) });
  }
  let manifest: ManifestInfo | undefined;
  if (manifestEntry && !manifestEntry.directory) {
    let text: string | null = null;
    try { text = decodeText(await manifestEntry.getData(new zip.Uint8ArrayWriter())); } catch { text = null; }
    manifest = attachManifest(items, baseName(manifestEntry.filename), text);
  }
  return finish(stripExt(file.name), items, skipped, ignored, manifest);
}

export interface PickedFile { file: File; path: string }

/** A folder (input webkitdirectory, drag and drop) or a set of files. */
export async function scanFiles(files: PickedFile[], cfg: ImportConfig, sourceName: string): Promise<ScanResult> {
  const items: ScanItem[] = [];
  const skipped: SkippedItem[] = [];
  let ignored = 0;
  let manifestFile: PickedFile | null = null;
  for (const f of files) {
    if (isJunk(f.path)) { ignored++; continue; }
    if (MANIFEST_NAMES.has(baseName(f.path).toLowerCase())) {
      if (!manifestFile || depth(f.path) < depth(manifestFile.path)) manifestFile = f;
      continue;
    }
    const bad = classify(f.path, f.file.size, cfg);
    if (bad) { skipped.push(bad); continue; }
    const file = f.file;
    items.push({ path: f.path, name: baseName(f.path), size: file.size, date: new Date(file.lastModified), read: async () => file });
  }
  let manifest: ManifestInfo | undefined;
  if (manifestFile) {
    let text: string | null = null;
    try { text = decodeText(new Uint8Array(await manifestFile.file.arrayBuffer())); } catch { text = null; }
    manifest = attachManifest(items, baseName(manifestFile.path), text);
  }
  return finish(sourceName, items, skipped, ignored, manifest);
}

export function pickedFromInput(list: FileList | null): PickedFile[] {
  return Array.from(list ?? []).map((file) => ({ file, path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name }));
}

/**
 * What was dropped: one .zip, or files and folders (walked recursively). The entries must be taken while the drop
 * event runs, so call this synchronously from the handler and await the result.
 */
export function readDrop(dt: DataTransfer): Promise<{ zip?: File; files: PickedFile[] }> {
  const entries = Array.from(dt.items ?? [])
    .map((i) => (typeof i.webkitGetAsEntry === "function" ? i.webkitGetAsEntry() : null))
    .filter((e): e is FileSystemEntry => !!e);
  const plain = Array.from(dt.files ?? []);
  return (async () => {
    if (entries.length === 0) {
      if (plain.length === 1 && plain[0].name.toLowerCase().endsWith(".zip")) return { zip: plain[0], files: [] };
      return { files: plain.map((file) => ({ file, path: file.name })) };
    }
    if (entries.length === 1 && entries[0].isFile && entries[0].name.toLowerCase().endsWith(".zip") && plain[0]) return { zip: plain[0], files: [] };
    const out: PickedFile[] = [];
    const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
      if (entry.isFile) {
        const file = await new Promise<File>((ok, fail) => (entry as FileSystemFileEntry).file(ok, fail));
        out.push({ file, path: prefix + entry.name });
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        for (;;) {
          const batch = await new Promise<FileSystemEntry[]>((ok, fail) => reader.readEntries(ok, fail));
          if (batch.length === 0) break;
          for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
        }
      }
    };
    for (const e of entries) await walk(e, "");
    return { files: out };
  })();
}

// ── running ────────────────────────────────────────────────────────────────────────────────

export interface RunProgress {
  total: number;
  done: number;
  imported: number;
  duplicates: number;
  failed: number;
  sentBytes: number;
  /** Documents per second over the last half minute. */
  rate: number;
  etaSeconds: number | null;
  paused: boolean;
}

export interface RunError { path: string; code: string; detail?: string }

export type RunOutcome =
  | { kind: "completed" }
  | { kind: "cancelled" }
  | { kind: "stopped"; reason: "session" | "not_allowed" | "closed"; message?: string };

interface BatchResult { index: number; status: "imported" | "duplicate" | "failed"; code?: string; detail?: string }

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Manifest dates without a time zone ("15/03/2024 10:30", "2024-03-15") are the customer's local time: they go to the
 * API as UTC instants, so a contract signed at 10:30 in Brazil shows 10:30, not 07:30. Anything else is sent as typed.
 */
export function localDateToIso(value?: string): string | undefined {
  const s = value?.trim();
  if (!s) return undefined;
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(s);
  const build = (y: number, mo: number, d: number, h = 0, mi = 0, se = 0) => {
    const date = new Date(y, mo - 1, d, h, mi, se);
    return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d ? date.toISOString() : s;
  };
  if (m) return build(+m[3], +m[2], +m[1], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(s);
  if (m) return build(+m[1], +m[2], +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  return s;
}

/** A file date worth keeping: zip tools write 1980-01-01 when they have none. */
function usableDate(d?: Date): string | undefined {
  if (!d || isNaN(d.getTime())) return undefined;
  if (d.getFullYear() <= 1980 || d.getTime() > Date.now() + 86_400_000) return undefined;
  return d.toISOString();
}

export class ImportRunner {
  private next = 0;
  private paused = false;
  private cancelled = false;
  private outcome: RunOutcome | null = null;
  private waiters: (() => void)[] = [];
  private seen = new Set<string>();
  private nextSlot = 0;
  private samples: { t: number; d: number }[] = [];
  private pendingErrors: RunError[] = [];
  private p: RunProgress;
  readonly errors: RunError[] = [];

  constructor(
    private readonly opts: {
      importId: string;
      items: ScanItem[];
      skipped: SkippedItem[];
      /** false when continuing an import: its report already has these files from the first run. */
      reportSkipped?: boolean;
      cfg: ImportConfig;
      concurrency?: number;
      onUpdate: (progress: RunProgress, errors: RunError[]) => void;
    },
  ) {
    this.p = { total: opts.items.length + opts.skipped.length, done: 0, imported: 0, duplicates: 0, failed: 0, sentBytes: 0, rate: 0, etaSeconds: null, paused: false };
  }

  get isPaused() { return this.paused; }

  pause() { this.paused = true; this.emit(); }

  resume() {
    this.paused = false;
    this.waiters.splice(0).forEach((w) => w());
    this.emit();
  }

  cancel() { this.cancelled = true; this.resume(); }

  async run(): Promise<RunOutcome> {
    const { skipped } = this.opts;
    for (const s of skipped) this.fail(s.path, s.code, s.detail, this.opts.reportSkipped !== false);
    await this.flushErrors();
    const timer = setInterval(() => this.emit(), 500);
    let wakeLock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } };
    const keepAwake = async () => { try { wakeLock = (await nav.wakeLock?.request("screen")) ?? null; } catch { wakeLock = null; } };
    const onVisible = () => { if (document.visibilityState === "visible") void keepAwake(); };
    await keepAwake();
    document.addEventListener("visibilitychange", onVisible);
    try {
      await Promise.all(Array.from({ length: Math.max(1, this.opts.concurrency ?? 3) }, () => this.worker()));
      await this.flushErrors();
    } finally {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      try { await (wakeLock as { release: () => Promise<void> } | null)?.release(); } catch { /* already released */ }
    }
    if (this.outcome) { this.emit(); return this.outcome; }
    try { await importApi.complete(this.opts.importId, this.cancelled); } catch { /* the import stays open; a re-run continues it */ }
    this.emit();
    return this.cancelled ? { kind: "cancelled" } : { kind: "completed" };
  }

  private async worker() {
    while (!this.cancelled && !this.outcome) {
      if (this.paused) { await new Promise<void>((r) => this.waiters.push(r)); continue; }
      const batch = this.take();
      if (!batch) return;
      await this.process(batch);
    }
  }

  /** Up to maxFilesPerBatch files and 20 MB; a file bigger than that goes alone. */
  private take(): ScanItem[] | null {
    const { items, cfg } = this.opts;
    if (this.next >= items.length) return null;
    const cap = Math.min(cfg.maxBatchBytes, 20 * 1024 * 1024);
    const batch: ScanItem[] = [];
    let bytes = 0;
    while (this.next < items.length && batch.length < cfg.maxFilesPerBatch) {
      const it = items[this.next];
      if (batch.length > 0 && bytes + it.size > cap) break;
      batch.push(it);
      bytes += it.size;
      this.next++;
    }
    return batch;
  }

  private async process(batch: ScanItem[]) {
    const ready: { item: ScanItem; blob: Blob; key: string }[] = [];
    for (const item of batch) {
      try {
        const blob = await item.read();
        if (blob.size === 0) { this.fail(item.path, "empty"); continue; }
        if (blob.size > this.opts.cfg.maxFileBytes) { this.fail(item.path, "too_large", mb(blob.size)); continue; }
        const key = await sha256(blob);
        if (this.seen.has(key)) { this.duplicate(); continue; }
        this.seen.add(key);
        ready.push({ item, blob, key });
      } catch (e) {
        this.fail(item.path, "unreadable", e instanceof Error ? e.message : String(e));
      }
    }
    if (ready.length === 0) return;

    // Files this account already has (an earlier run of the same zip) are not uploaded again.
    const check = await this.post<{ existing: string[] }>(`/api/documents/imports/${this.opts.importId}/check`, { keys: ready.map((r) => r.key) });
    if (this.outcome) return;
    const existing = new Set(check.ok ? check.data.existing : []);
    const send = ready.filter((r) => !existing.has(r.key));
    for (let i = send.length; i < ready.length; i++) this.duplicate();
    if (send.length === 0) return;

    const form = new FormData();
    for (const r of send) form.append("files", r.blob, r.item.name);
    form.append("meta", JSON.stringify(send.map((r) => ({
      path: r.item.path,
      title: r.item.meta?.title || stripExt(r.item.name),
      date: localDateToIso(r.item.meta?.date) || usableDate(r.item.date),
      status: r.item.meta?.status,
      externalId: r.item.meta?.externalId,
      signers: r.item.meta?.signers?.map((s) => ({ ...s, signedAt: localDateToIso(s.signedAt) })),
      key: r.key,
    }))));
    const res = await this.post<{ results: BatchResult[] }>(`/api/documents/imports/${this.opts.importId}/files`, form);
    if (this.outcome) return;
    if (!res.ok) {
      for (const r of send) this.fail(r.item.path, res.code, res.message);
      return;
    }
    const byIndex = new Map(res.data.results.map((r) => [r.index, r]));
    send.forEach((r, i) => {
      const result = byIndex.get(i);
      if (result?.status === "imported") { this.p.imported++; this.p.done++; this.p.sentBytes += r.blob.size; }
      else if (result?.status === "duplicate") this.duplicate();
      // Recorded by the server already: only counted here.
      else { this.p.failed++; this.p.done++; this.errors.push({ path: r.item.path, code: result?.code || "failed", detail: result?.detail }); }
    });
  }

  /** About 4 requests a second across all workers: under the API's 300 per minute for imports. */
  private async pace() {
    const now = Date.now();
    const slot = Math.max(now, this.nextSlot);
    this.nextSlot = slot + 250;
    if (slot > now) await sleep(slot - now);
  }

  /** POST with retries: busy/rate-limited waits, network and 5xx back off; 401/403/409 stop the run. */
  private async post<T>(path: string, body: FormData | object): Promise<{ ok: true; data: T } | { ok: false; code: string; message?: string }> {
    let failures = 0;
    for (let waits = 0; waits < 60 && !this.outcome; ) {
      await this.pace();
      let res: Response;
      try {
        const isForm = body instanceof FormData;
        res = await fetch(getApiUrl(path), {
          method: "POST",
          headers: isForm ? authHeaders() : { ...authHeaders(), "Content-Type": "application/json" },
          body: isForm ? body : JSON.stringify(body),
        });
      } catch {
        if (++failures >= 5) return { ok: false, code: "network" };
        await sleep(1000 * 2 ** failures);
        continue;
      }
      if (res.ok) return { ok: true, data: unwrap<T>(await res.json()) };
      if (res.status === 401) { this.stop("session"); return { ok: false, code: "session" }; }
      if (res.status === 403) { this.stop("not_allowed", (await errorFrom(res)).message); return { ok: false, code: "not_allowed" }; }
      if (res.status === 409) { this.stop("closed"); return { ok: false, code: "closed" }; }
      if (res.status === 429 || res.status === 503) {
        waits++;
        let seconds = res.status === 429 ? 20 : 3;
        try { const b = await res.json(); if (typeof b?.retryAfter === "number") seconds = Math.min(60, b.retryAfter); } catch { /* no body */ }
        await sleep(seconds * 1000);
        continue;
      }
      if (res.status >= 500) {
        if (++failures >= 5) return { ok: false, code: "server_error", message: `HTTP ${res.status}` };
        await sleep(1000 * 2 ** failures);
        continue;
      }
      const err = await errorFrom(res);
      return { ok: false, code: err.code?.toLowerCase() || "rejected", message: err.message };
    }
    return { ok: false, code: "busy" };
  }

  private stop(reason: "session" | "not_allowed" | "closed", message?: string) {
    if (!this.outcome) this.outcome = { kind: "stopped", reason, message };
    this.resume();
  }

  private duplicate() { this.p.duplicates++; this.p.done++; }

  /** A file the browser could not send: counted, shown and (unless already there) sent to the server's error report. */
  private fail(path: string, code: string, detail?: string, report = true) {
    this.p.failed++;
    this.p.done++;
    const e = { path, code, detail };
    this.errors.push(e);
    if (!report) return;
    this.pendingErrors.push(e);
    if (this.pendingErrors.length >= 200) void this.flushErrors();
  }

  private async flushErrors() {
    while (this.pendingErrors.length > 0) {
      const chunk = this.pendingErrors.splice(0, 500);
      try {
        await fetch(getApiUrl(`/api/documents/imports/${this.opts.importId}/errors`), {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ items: chunk }),
        });
      } catch { /* the report misses them; the screen still lists them */ }
    }
  }

  private emit() {
    const now = Date.now();
    this.samples.push({ t: now, d: this.p.done });
    while (this.samples.length > 2 && now - this.samples[0].t > 30_000) this.samples.shift();
    const first = this.samples[0];
    const span = (now - first.t) / 1000;
    this.p.rate = span >= 2 ? (this.p.done - first.d) / span : 0;
    const left = this.p.total - this.p.done;
    this.p.etaSeconds = this.p.rate > 0 ? Math.round(left / this.p.rate) : null;
    this.p.paused = this.paused;
    this.opts.onUpdate({ ...this.p }, this.errors);
  }
}
